import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Link,
  useLocation,
} from "react-router-dom";
import { useState, useEffect } from "react";
import AffiliatePage, { AffiliateProgram } from "./pages/AffiliatePage";
import Metadata from "./seo/Metadata";
import ProductInfoPage from "./pages/ProductInfoPage";
import ThemeToggle from "./components/ThemeToggle";
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import SearchPage from "./pages/SearchPage";
import MyLeadsPage from "./pages/MyLeadsPage";
import BillingPage from "./pages/BillingPage";
import AdminPage from "./pages/AdminPage";
import TermsPage from "./pages/TermsPage";
import PrivacyPage from "./pages/PrivacyPage";
import OptOutPage from "./pages/OptOutPage";
import PricingPage from "./pages/PricingPage";
import SavedSearchesPage from "./pages/SavedSearchesPage";
import DownloadsPage from "./pages/DownloadsPage";
import HelpPage from "./pages/HelpPage";
import WorkspaceShell from "./components/WorkspaceShell";
import { apiFetch } from "./services/api";
import { User } from "./types";
function ScrollToPage() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) document.getElementById(hash.slice(1))?.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

function ReferralCapture(){const {search}=useLocation();useEffect(()=>{const code=new URLSearchParams(search).get('ref');if(code)void apiFetch('/api/affiliates/referral',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({code})}).catch(()=>{});},[search]);return null;}
function SignedInRedirect({fallback}:{fallback:string}){const {search}=useLocation();return <Navigate to={new URLSearchParams(search).get('next')==='affiliate'?'/affiliate':fallback}/>;}
function RefundPage() {
  return (
    <main className="legal-main">
      <div className="legal-content">
        <h1>Refund Policy</h1>
        <p>
          Subscriptions can be canceled through the Stripe Customer Portal. For
          billing issues or a refund request, contact{" "}
          <a href="mailto:support@reachatlasleads.com">
            support@reachatlasleads.com
          </a>
          .
        </p>
        <Link to="/">Back home</Link>
      </div>
    </main>
  );
}
function App() {
  const [user, setUser] = useState<User | null>(null),
    [loading, setLoading] = useState(true);
  useEffect(() => {
    const refresh = () => {
      apiFetch("/api/auth/me")
        .then(async (r) => {
          if (r.ok) setUser((await r.json()).user);
          else if (r.status === 401) setUser(null);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    };
    refresh();
    window.addEventListener("reachatlas:account", refresh);
    return () => window.removeEventListener("reachatlas:account", refresh);
  }, []);
  if (loading)
    return (
      <div className="app-loading">
        <span className="loading-compass">◈</span>Loading your next opportunity…
      </div>
    );
  return (
    <BrowserRouter>
      <Metadata /><ThemeToggle /><ScrollToPage /><ReferralCapture />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/explore" element={<SearchPage publicMode />} />
        <Route path="/pricing" element={<PricingPage user={user} />} />
        <Route
          path="/login"
          element={
            user ? (
              <SignedInRedirect fallback="/dashboard" />
            ) : (
              <LoginPage onLogin={setUser} />
            )
          }
        />
        <Route
          path="/signup"
          element={
            user ? (
              <SignedInRedirect fallback="/pricing" />
            ) : (
              <LoginPage initialMode="register" onLogin={setUser} />
            )
          }
        />
        <Route
          element={
            user ? (
              <WorkspaceShell user={user} onLogout={() => setUser(null)} />
            ) : (
              <Navigate to="/login" />
            )
          }
        >
          <Route
            path="/dashboard"
            element={user && <DashboardPage user={user} />}
          />
          <Route path="/search" element={user && <SearchPage user={user} />} />
          <Route
            path="/my-leads"
            element={user && <MyLeadsPage user={user} />}
          />
          <Route path="/saved-searches" element={<SavedSearchesPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route
            path="/billing"
            element={user && <BillingPage user={user} />}
          />
        </Route>
        <Route
          path="/help"
          element={
            user ? (
              <WorkspaceShell user={user} onLogout={() => setUser(null)} />
            ) : (
              <HelpPage publicMode />
            )
          }
        >
          {user && <Route index element={<HelpPage />} />}
        </Route>
        <Route
          path="/admin"
          element={
            user?.role === "admin" ? (
              <AdminPage onLogout={() => setUser(null)} />
            ) : (
              <LoginPage admin onLogin={setUser} />
            )
          }
        />
        <Route path="/about" element={<ProductInfoPage />} /><Route path="/faq" element={<ProductInfoPage faq />} />
        <Route path="/affiliates" element={<AffiliateProgram />} /><Route path="/affiliate" element={user ? <AffiliatePage /> : <Navigate to="/login?next=affiliate" />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/opt-out" element={<OptOutPage />} />
        <Route path="/refund" element={<RefundPage />} />
        <Route
          path="*"
          element={
            <main className="public-section empty-state">
              <h1>Let's get you back on track.</h1>
              <Link to="/">Go to ReachAtlas →</Link>
            </main>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}
export default App;
